import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { API_URL } from "../../confi";
import contactConfig from "../../confi";
import "./sub1.css";
import "./blog-details.css";

// Convert blog title into SEO-friendly URL slug
const slugify = (title) =>
  title
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "") || "";

const predefinedCategories = [
  "Housekeeping",
  "Baby Sitting",
  "Elderly Care",
  "Patient Care",
  "Cook",
  "Driver",
];

const BlogDetails = () => {
  const { title } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [blog, setBlog] = useState(null);
  const [allBlogs, setAllBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [copied, setCopied] = useState(false);

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const getExcerpt = (content, length = 150) => {
    if (!content) return "";
    const text = content.replace(/<[^>]*>/g, "");
    return text.length > length ? text.substring(0, length) + "..." : text;
  };

  // Strip duplicate blog title from the start of content
  const cleanBlogContent = (content, blogTitle) => {
    if (!content) return "";
    let cleaned = content.trim();

    const normalize = (t) =>
      (t || "")
        .toLowerCase()
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/[^a-z0-9]/g, "")
        .trim();

    const normalizedBlogTitle = normalize(blogTitle);

    // If content starts with an h1, h2, h3, h4, or p/div containing the title
    const headingMatch = cleaned.match(
      /^(\s*<(?:h1|h2|h3|h4|p|div)[^>]*>([\s\S]*?)<\/(?:h1|h2|h3|h4|p|div)>\s*)/i
    );
    if (headingMatch) {
      const headingText = normalize(headingMatch[2]);
      if (
        normalizedBlogTitle &&
        headingText &&
        (headingText === normalizedBlogTitle ||
          normalizedBlogTitle.includes(headingText) ||
          headingText.includes(normalizedBlogTitle))
      ) {
        cleaned = cleaned.slice(headingMatch[0].length);
      }
    }

    // Strip leading empty paragraphs, empty spaces, and excessive line breaks
    cleaned = cleaned.replace(/^(\s*<p[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+/gi, "");

    return cleaned.trim();
  };

  const transformRawBlog = (b) => {
    const matchedCategory =
      predefinedCategories.find(
        (cat) => cat.toLowerCase() === (b.author_name || b.category || "").toLowerCase()
      ) || b.category || b.author_name || "Housekeeping";

    return {
      id: b.id,
      title: b.title,
      content: b.content || "",
      author: b.author || b.author_name || "Admin",
      date: b.created_at || b.date || new Date().toISOString(),
      category: matchedCategory,
      image: b.image?.startsWith("http")
        ? b.image
        : b.image
        ? `${API_URL}/${b.image}`
        : `https://picsum.photos/800/400?random=${b.id}`,
      excerpt: getExcerpt(b.content),
      title_tag: b.title_tag || b.meta_title || "",
      meta_description: b.meta_description || "",
      keywords: b.keywords || b.meta_keywords || "",
      url_title: b.url_title || b.slug || slugify(b.title),
      canonical_url: b.canonical_url || "",
      og_title: b.og_title || b.title_tag || b.meta_title || b.title,
      og_description: b.og_description || b.meta_description || getExcerpt(b.content),
      meta_robots: b.meta_robots || "index, follow",
    };
  };

  // Fetch all blogs
  const fetchAllBlogs = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/blogs`, {
        headers: { Accept: "application/json" },
      });
      const data = await res.json();

      if (res.ok) {
        const blogsData = data.blogs?.data || data.blogs || data.data || [];
        const transformed = blogsData.map(transformRawBlog);
        setAllBlogs(transformed);

        // Find active blog by URL slug or title slug
        const found = transformed.find((b) => {
          const bSlug = (b.url_title || slugify(b.title)).toLowerCase();
          const rawSlug = slugify(b.title).toLowerCase();
          const targetSlug = (title || "").toLowerCase();
          return bSlug === targetSlug || rawSlug === targetSlug;
        });

        if (found) {
          setBlog(found);
          setError(null);
        } else if (location.state?.blog) {
          setBlog(transformRawBlog(location.state.blog));
          setError(null);
        } else {
          setError("Blog post not found");
        }
      } else {
        setError(data.message || "Failed to load blog post");
      }
    } catch (err) {
      console.error("Error fetching blogs:", err);
      setError("Failed to load blog post");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // When title param changes, reload or search from existing allBlogs
    if (allBlogs.length > 0) {
      const found = allBlogs.find((b) => {
        const bSlug = (b.url_title || slugify(b.title)).toLowerCase();
        const rawSlug = slugify(b.title).toLowerCase();
        const targetSlug = (title || "").toLowerCase();
        return bSlug === targetSlug || rawSlug === targetSlug;
      });
      if (found) {
        setBlog(found);
        setLoading(false);
      } else {
        fetchAllBlogs();
      }
    } else {
      fetchAllBlogs();
    }
  }, [title]);

  // Sidebar Actions
  const handleSidebarSearch = (e) => {
    e.preventDefault();
    if (sidebarSearch.trim()) {
      navigate("/blog", { state: { search: sidebarSearch.trim() } });
    }
  };

  const handleCategoryClick = (catName) => {
    navigate("/blog", { state: { category: catName } });
  };

  const handleRecentPostClick = (post) => {
    const slug = post.url_title || slugify(post.title);
    navigate(`/blog/${slug}`, { state: { blogId: post.id, blog: post } });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleCall = () => {
    window.open(`tel:${contactConfig.phone}`);
  };

  const handleWhatsApp = () => {
    const phoneNumber = `${contactConfig.phone}`;
    const message = `Hello, I read your article "${blog?.title || "Blog"}" and would like to know more about your services.`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  // Recent posts for sidebar
  const recentPosts = allBlogs
    .filter((b) => b.id !== blog?.id)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);

  // Category counts
  const categoryStats = predefinedCategories.map((cat) => {
    const count = allBlogs.filter(
      (b) => (b.category || "").toLowerCase() === cat.toLowerCase()
    ).length;
    return { name: cat, count };
  });

  // Related blogs
  const relatedBlogs = allBlogs
    .filter((b) => b.category === blog?.category && b.id !== blog?.id)
    .slice(0, 3);

  if (loading) {
    return (
      <div className="main-content-wrapper">
        <div className="blog-details-page-wrapper">
          <div className="container" style={{ padding: "80px 20px", textAlign: "center" }}>
            <div className="loading-spinner" style={{ margin: "0 auto 20px" }}></div>
            <p style={{ fontSize: "1.1rem", color: "#64748b" }}>Loading blog post...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="main-content-wrapper">
        <div className="blog-details-page-wrapper">
          <div className="container" style={{ padding: "80px 20px", textAlign: "center" }}>
            <h2 style={{ fontSize: "2rem", color: "#1e1b4b", marginBottom: "12px" }}>Blog Not Found</h2>
            <p style={{ color: "#64748b", marginBottom: "24px" }}>
              {error || "The blog post you're looking for does not exist or has been removed."}
            </p>
            <Link to="/blog" className="breadcrumb-back-btn">
              ← Back to All Blogs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const currentUrl =
    blog.canonical_url || `https://jphomeservice.in/blog/${blog.url_title || title}`;
  const seoTitle = blog.title_tag || blog.title;
  const seoDesc = blog.meta_description || blog.excerpt || "Read this insightful article from J P Home Services";
  const ogTitle = blog.og_title || seoTitle;
  const ogDesc = blog.og_description || seoDesc;
  const ogImage = blog.image;

  return (
    <div className="main-content-wrapper">
      {/* Dynamic SEO, Open Graph & Twitter Cards */}
      <Helmet>
        <title>{seoTitle.includes("J P Home") ? seoTitle : `${seoTitle} | J P Home Services`}</title>
        <meta name="description" content={seoDesc} />
        <meta
          name="keywords"
          content={blog.keywords || `${blog.category}, maid service, home services ahmedabad`}
        />
        <meta name="robots" content={blog.meta_robots || "index, follow"} />
        <link rel="canonical" href={currentUrl} />

        {/* Open Graph Tags (Facebook / WhatsApp / LinkedIn) */}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="J P Home Services" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDesc} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:secure_url" content={ogImage} />
        <meta property="og:image:alt" content={blog.title} />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDesc} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:image:alt" content={blog.title} />
      </Helmet>

      {/* Floating Quick Action Icons */}
      <div className="floating-contact-icons">
        <div className="contact-icon whatsapp-icon" onClick={handleWhatsApp} title="Chat on WhatsApp">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893c0-3.18-1.24-6.169-3.495-8.424"/>
          </svg>
        </div>
        <div className="contact-icon call-icon" onClick={handleCall} title="Call Us Directly">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 10.999h2C22 5.869 18.127 2 12.99 2v2C17.052 4 20 6.943 20 10.999z" />
            <path d="M13 8c2.103 0 3 .897 3 3h2c0-3.225-1.775-5-5-5v2zm3.422 5.443a1.001 1.001 0 00-1.391.043l-2.393 2.461c-.576-.11-1.734-.471-2.926-1.66-1.192-1.193-1.553-2.354-1.66-2.926l2.459-2.394a1 1 0 00.043-1.391L6.859 3.513a1 1 0 00-1.391-.087l-2.17 1.861a1 1 0 00-.29.649c-.015.25-.301 6.172 4.291 10.766C11.305 20.707 16.323 21 17.705 21c.202 0 .326-.006.359-.008a.992.992 0 00.648-.291l1.86-2.171a1 1 0 00-.086-1.391l-4.064-3.696z" />
          </svg>
        </div>
      </div>

      <div className="blog-details-page-wrapper">
        {/* Top Breadcrumb Bar */}
        <div className="blog-details-breadcrumb-bar">
          <div className="container">
            <div className="breadcrumb-content">
              <nav className="breadcrumb-links">
                <Link to="/">Home</Link>
                <span className="breadcrumb-separator">›</span>
                <Link to="/blog">Blog</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">{blog.title}</span>
              </nav>
              <Link to="/blog" className="breadcrumb-back-btn">
                ← Back to Blogs
              </Link>
            </div>
          </div>
        </div>

        {/* Two-Column Layout Container */}
        <div className="container">
          <div className="blog-details-grid">
            {/* =========================================================
                LEFT SIDE: Featured Image & Content Part
                ========================================================= */}
            <main className="blog-main-content">
              {/* Category & Read Time */}
              <div className="article-top-meta">
                <span className="article-category-badge">{blog.category}</span>
                <span className="article-reading-time">📖 4 min read</span>
              </div>

              {/* Main Heading */}
              <h1 className="article-title-heading">{blog.title}</h1>

              {/* Author & Date Row */}
              <div className="article-author-date-row">
                <div className="author-date-left">
                  <div className="author-avatar-circle">
                    {blog.author?.charAt(0)?.toUpperCase() || "J"}
                  </div>
                  <div className="author-info-text">
                    <span className="author-name-text">By {blog.author}</span>
                    <span className="post-published-date">{formatDate(blog.date)}</span>
                  </div>
                </div>
                <div className="share-buttons-group">
                  <span className="share-label-title">Share:</span>
                  <a
                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(blog.title + " - " + currentUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-icon-btn whatsapp"
                    title="Share on WhatsApp"
                  >
                    WhatsApp
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-icon-btn facebook"
                    title="Share on Facebook"
                  >
                    Facebook
                  </a>
                  <button
                    onClick={handleCopyLink}
                    className="share-icon-btn copy"
                    title="Copy Article Link"
                  >
                    {copied ? "✓ Copied!" : "🔗 Copy Link"}
                  </button>
                </div>
              </div>

              {/* Featured Image Part */}
              <div className="article-featured-image-box">
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="article-featured-img"
                  onError={(e) => {
                    e.target.src = `https://picsum.photos/800/400?random=${blog.id}`;
                  }}
                />
              </div>

              {/* Content Part */}
              <div className="article-body-content">
                {blog.content ? (
                  <div dangerouslySetInnerHTML={{ __html: cleanBlogContent(blog.content, blog.title) }} />
                ) : (
                  <div className="content-fallback">
                    <p>This blog post content is coming soon. Please check back later for the full article.</p>
                  </div>
                )}
              </div>

              {/* Bottom Share & Footer */}
              <div className="article-share-footer">
                <div className="share-buttons-group">
                  <span className="share-label-title">Liked this article? Share it:</span>
                  <a
                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(blog.title + " - " + currentUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-icon-btn whatsapp"
                  >
                    WhatsApp
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-icon-btn facebook"
                  >
                    Facebook
                  </a>
                  <button onClick={handleCopyLink} className="share-icon-btn copy">
                    {copied ? "✓ Link Copied!" : "🔗 Copy Link"}
                  </button>
                </div>
                <Link to="/blog" className="breadcrumb-back-btn">
                  ← Back to Blogs
                </Link>
              </div>
            </main>

            {/* =========================================================
                RIGHT SIDE: Sidebar (Search, Categories, Recent Posts, CTA)
                ========================================================= */}
            <aside className="blog-sidebar-wrap">
              {/* Widget 1: Search */}
              <div className="sidebar-widget">
                <div className="sidebar-widget-header">
                  <h3 className="sidebar-widget-title">
                    <span>🔍</span> Search
                  </h3>
                  <div className="sidebar-title-underline"></div>
                </div>
                <form onSubmit={handleSidebarSearch} className="sidebar-search-form">
                  <input
                    type="text"
                    className="sidebar-search-input"
                    placeholder="Search articles..."
                    value={sidebarSearch}
                    onChange={(e) => setSidebarSearch(e.target.value)}
                  />
                  <button type="submit" className="sidebar-search-btn" title="Search">
                    →
                  </button>
                </form>
              </div>

              {/* Widget 2: Recent Posts */}
              <div className="sidebar-widget">
                <div className="sidebar-widget-header">
                  <h3 className="sidebar-widget-title">
                    <span>🕒</span> Recent Posts
                  </h3>
                  <div className="sidebar-title-underline"></div>
                </div>
                <div className="sidebar-recent-posts">
                  {recentPosts.length === 0 ? (
                    <p style={{ color: "#94a3b8", fontSize: "0.88rem" }}>No other posts available.</p>
                  ) : (
                    recentPosts.map((post) => (
                      <div
                        key={post.id}
                        className="recent-post-card"
                        onClick={() => handleRecentPostClick(post)}
                      >
                        <div className="recent-post-thumb-wrap">
                          <img
                            src={post.image}
                            alt={post.title}
                            className="recent-post-thumb"
                            onError={(e) => {
                              e.target.src = `https://picsum.photos/150/150?random=${post.id}`;
                            }}
                          />
                        </div>
                        <div className="recent-post-info">
                          <h4 className="recent-post-title">{post.title}</h4>
                          <span className="recent-post-date">
                            📅 {formatDate(post.date)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Widget 3: Categories */}
              <div className="sidebar-widget">
                <div className="sidebar-widget-header">
                  <h3 className="sidebar-widget-title">
                    <span>📁</span> Categories
                  </h3>
                  <div className="sidebar-title-underline"></div>
                </div>
                <ul className="sidebar-categories-list">
                  {categoryStats.map((cat) => (
                    <li key={cat.name}>
                      <div
                        className={`sidebar-category-item ${
                          blog.category?.toLowerCase() === cat.name.toLowerCase() ? "active" : ""
                        }`}
                        onClick={() => handleCategoryClick(cat.name)}
                      >
                        <div className="category-name-wrap">
                          <span className="category-arrow-icon">▶</span>
                          <span>{cat.name}</span>
                        </div>
                        <span className="category-count-badge">{cat.count}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Widget 4: Quick Help & Booking CTA */}
              <div className="sidebar-cta-widget">
                <div className="cta-widget-icon">🏡</div>
                <h3 className="cta-widget-title">Need Help at Home?</h3>
                <p className="cta-widget-desc">
                  Hire verified, reliable maids, cooks, babysitters & patient care assistants across Ahmedabad.
                </p>
                <div className="cta-widget-actions">
                  <button onClick={handleCall} className="cta-btn-call">
                    📞 Call Now
                  </button>
                  <button onClick={handleWhatsApp} className="cta-btn-whatsapp">
                    💬 WhatsApp Us
                  </button>
                </div>
              </div>
            </aside>
          </div>

          {/* Related Articles Section */}
          {relatedBlogs.length > 0 && (
            <section className="related-posts" style={{ marginTop: "60px" }}>
              <h2 className="related-title" style={{ fontSize: "1.8rem", color: "#1e1b4b", marginBottom: "24px" }}>
                Related Articles
              </h2>
              <div className="related-grid">
                {relatedBlogs.map((relatedBlog) => (
                  <div
                    key={relatedBlog.id}
                    className="related-card"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRecentPostClick(relatedBlog)}
                  >
                    <div className="related-image">
                      <img
                        src={relatedBlog.image}
                        alt={relatedBlog.title}
                        onError={(e) => {
                          e.target.src = `https://picsum.photos/400/250?random=${relatedBlog.id}`;
                        }}
                      />
                      <span className="related-category">{relatedBlog.category}</span>
                    </div>
                    <div className="related-content">
                      <h3 className="related-card-title">{relatedBlog.title}</h3>
                      <p className="related-excerpt">{relatedBlog.excerpt}</p>
                      <div className="related-meta">
                        <span className="related-date">{formatDate(relatedBlog.date)}</span>
                        <span className="read-more-link" style={{ color: "#7e22ce", fontWeight: "600" }}>
                          Read More →
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogDetails;